import { useState, useEffect, useCallback } from 'react'
import { format } from 'date-fns'
import {
  Package,
  Plus,
  Eye,
  Edit,
  RefreshCw,
  Download,
  Search,
  Filter,
  Calendar,
  Building,
  ArrowRightLeft,
  User,
  Truck,
  FileText,
  DollarSign,
  CheckCircle,
  AlertCircle,
  X,
  TrendingUp,
  RotateCcw,
  ChevronDown
} from 'lucide-react'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card'
import InlineLoader from '../../../components/ui/InlineLoader'
import ButtonSpinner from '../../../components/ui/ButtonSpinner'
import supplierReturnService from '../../../services/supplierReturnService'
import purchaseOrderService from '../../../services/purchaseOrderService'
import branchService from '../../../services/branchService'
import warehouseService from '../../../services/warehouseService'
import AuthService from '../../../services/authService'

// Toast Component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2 animate-in slide-in-from-right-full duration-300 ${
    type === 'success' 
      ? 'bg-green-500 text-white' 
      : type === 'error'
      ? 'bg-red-500 text-white'
      : type === 'warning'
      ? 'bg-yellow-500 text-white'
      : 'bg-blue-500 text-white'
  }`}>
    {type === 'success' ? <CheckCircle className="h-5 w-5" /> : 
     type === 'error' ? <AlertCircle className="h-5 w-5" /> : 
     <AlertCircle className="h-5 w-5" />}
    <span className="font-medium">{message || 'Action completed'}</span>
    <button onClick={onClose} className="ml-2 text-white/80 hover:text-white">
      <X className="h-4 w-4" />
    </button>
  </div>
)

import { isManagerRole } from '../../../config/roles'

// Helper function to get supplier or warehouse name from purchase order
const getSupplierOrWarehouseName = (po) => {
  if (!po) return 'Unknown'
  
  if (po.supplierType === 'warehouse') {
    return po.warehouse?.name || 'Unknown Warehouse'
  } else {
    return po.supplier?.name || 'Unknown Supplier'
  }
}

// Helper function to get supplier or warehouse name from supplier return record
const getSupplierOrWarehouseFromReturn = (supplierReturn) => {
  // First try to get from direct supplier/warehouse fields
  if (supplierReturn.supplier?.name) {
    return supplierReturn.supplier.name
  }
  if (supplierReturn.warehouse?.name) {
    return supplierReturn.warehouse.name
  }
  
  // Fallback to purchase order data if available
  if (supplierReturn.purchaseOrder) {
    return getSupplierOrWarehouseName(supplierReturn.purchaseOrder)
  }
  
  return 'Unknown Supplier/Warehouse'
}

const SupplierReturn = () => {
  const user = AuthService.getCurrentUser()
  const isManager = isManagerRole(user?.role)
  const userBranchId = user?.selectedBranch?._id || user?.branchId || user?.branch?._id

  // State
  const [supplierReturnNotes, setSupplierReturnNotes] = useState([])
  const [filteredSupplierReturnNotes, setFilteredSupplierReturnNotes] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [branchFilter, setBranchFilter] = useState(isManager ? (userBranchId || '') : '')
  const [toast, setToast] = useState(null)

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedSupplierReturn, setSelectedSupplierReturn] = useState(null)

  // Form data for create/edit
  const [formData, setFormData] = useState({
    purchaseOrder: '',
    supplier: '',
    returnBy: '',
    returnedDate: format(new Date(), 'yyyy-MM-dd'),
    returnedItems: [],
    note: ''
  })

  // Additional data
  const [purchaseOrders, setPurchaseOrders] = useState([])
  const [warehouses, setWarehouses] = useState([])
  const [branches, setBranches] = useState([])
  const [formLoading, setFormLoading] = useState(false)

  // Toast helper
  const showToast = useCallback((message, type = 'info') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 5000)
  }, [])

  const loadSupplierReturnNotes = useCallback(async () => {
    try {
      setLoading(true)
      const response = await supplierReturnService.getAllSupplierReturnNotes()
      
      // Service returns the data part directly, not the full response
      if (response && response.srns) {
        setSupplierReturnNotes(response.srns)
      } else {
        console.warn('No supplier return notes found in response')
        setSupplierReturnNotes([])
      }
    } catch (error) {
      console.error('Error loading supplier return notes:', error)
      showToast('Failed to load supplier return notes', 'error')
      setSupplierReturnNotes([])
    } finally {
      setLoading(false)
    }
  }, [showToast])

  const loadPurchaseOrders = useCallback(async () => {
    try {
      const response = await purchaseOrderService.getAllPurchaseOrders()
      if (response.success) {
        setPurchaseOrders(response.data?.purchaseOrders || [])
      }
    } catch (error) {
      console.error('Error loading purchase orders:', error)
      setPurchaseOrders([])
    }
  }, [])

  const loadWarehouses = useCallback(async () => {
    try {
      const response = await warehouseService.getAllWarehouses()
      if (response.success) {
        setWarehouses(response.data?.warehouses || [])
      }
    } catch (error) {
      console.error('Error loading warehouses:', error)
      setWarehouses([])
    }
  }, [])

  const loadBranches = useCallback(async () => {
    try {
      const response = await branchService.getAllBranches()
      if (response.success) {
        setBranches(response.data?.branches || [])
      }
    } catch (error) {
      console.error('Error loading branches:', error)
      setBranches([])
    }
  }, [])

  const applyFilters = useCallback(() => {
    let filtered = [...supplierReturnNotes]

    // Search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(supplierReturn => 
        supplierReturn.srnNumber?.toLowerCase().includes(search) ||
        supplierReturn.purchaseOrder?.poNumber?.toLowerCase().includes(search) ||
        getSupplierOrWarehouseFromReturn(supplierReturn)?.toLowerCase().includes(search) ||
        supplierReturn.returnBy?.toLowerCase().includes(search)
      )
    }

    // Branch filter
    if (branchFilter) {
      filtered = filtered.filter(supplierReturn => 
        supplierReturn.branch?._id === branchFilter
      )
    }

    setFilteredSupplierReturnNotes(filtered)
  }, [supplierReturnNotes, searchTerm, branchFilter])

  // Load data
  useEffect(() => {
    const loadData = async () => {
      await loadSupplierReturnNotes()
      await loadPurchaseOrders()
      await loadWarehouses()
      await loadBranches()
    }
    loadData()
  }, [loadSupplierReturnNotes, loadPurchaseOrders, loadWarehouses, loadBranches])

  // Apply filters
  useEffect(() => {
    applyFilters()
  }, [applyFilters])

  // Modal handlers
  const openCreateModal = () => {
    setFormData({
      purchaseOrder: '',
      supplier: '',
      returnBy: '',
      returnedDate: format(new Date(), 'yyyy-MM-dd'),
      returnedItems: [],
      note: ''
    })
    setShowCreateModal(true)
  }

  const openEditModal = (supplierReturn) => {
    setSelectedSupplierReturn(supplierReturn)
    setFormData({
      purchaseOrder: supplierReturn.purchaseOrder?._id || '',
      supplier: supplierReturn.supplier?._id || '',
      returnBy: supplierReturn.returnBy || '',
      returnedDate: supplierReturn.returnedDate ? format(new Date(supplierReturn.returnedDate), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
      returnedItems: supplierReturn.returnedItems || [],
      note: supplierReturn.note || ''
    })
    setShowEditModal(true)
  }

  const openViewModal = (supplierReturn) => {
    setSelectedSupplierReturn(supplierReturn)
    setShowViewModal(true)
  }

  // Handle purchase order selection
  const handlePOSelection = (poId) => {
    const selectedPO = purchaseOrders.find(po => po._id === poId)
    if (selectedPO) {
      // Try different possible item array names
      let itemsArray = selectedPO.purchaseOrderItems || selectedPO.items || selectedPO.orderItems || []
      
      // Map PO items to return items format
      const returnItems = itemsArray?.map(item => {
        // Try to get unit price from multiple possible sources
        const unitPrice = item.unitPrice || 
                         item.price || 
                         item.productSnapshot?.cost ||
                         item.productSnapshot?.price ||
                         item.rawMaterialSnapshot?.cost ||
                         item.rawMaterialSnapshot?.price ||
                         0
        
        return {
          // IDs for payload
          productId: item.product?._id || item.productId,
          rawMaterialId: item.rawMaterial?._id || item.rawMaterialId,
          // Return fields
          returnedQuantity: 0,
          unitPrice: unitPrice,
          // Keep reference to original quantities for validation
          orderedQuantity: item.quantity || item.orderedQuantity || 0,
          // Display fields
          itemType: item.itemType || (item.product ? 'product' : 'rawMaterial'),
          productSnapshot: item.product || item.productSnapshot,
          rawMaterialSnapshot: item.rawMaterial || item.rawMaterialSnapshot
        }
      }) || []

      setFormData(prev => ({
        ...prev,
        purchaseOrder: poId,
        supplier: selectedPO.supplier?._id || '',
        returnedItems: returnItems
      }))
    }
  }

  // Form submission
  const handleCreateSubmit = async (e) => {
    e.preventDefault()
    
    try {
      setFormLoading(true)
      
      // Validate form
      if (!formData.purchaseOrder) {
        showToast('Please select a purchase order', 'error')
        return
      }
      
      if (!formData.returnBy) {
        showToast('Please enter returned by', 'error')
        return
      }
      
      const hasValidItems = formData.returnedItems.some(item => 
        item.returnedQuantity && parseFloat(item.returnedQuantity) > 0
      )
      
      if (!hasValidItems) {
        showToast('Please add at least one item with valid quantity', 'error')
        return
      }
      
      // Transform data to match API payload structure
      const returnData = {
        purchaseOrder: formData.purchaseOrder,
        returnBy: formData.returnBy,
        returnedDate: formData.returnedDate,
        note: formData.note,
        returnedItems: formData.returnedItems
          .filter(item => item.returnedQuantity && parseFloat(item.returnedQuantity) > 0)
          .map(item => ({
            ...(item.productId && { productId: item.productId }),
            ...(item.rawMaterialId && { rawMaterialId: item.rawMaterialId }),
            returnedQuantity: parseFloat(item.returnedQuantity),
            unitPrice: parseFloat(item.unitPrice || 0)
          }))
      }

      // Submit form
      const response = await supplierReturnService.createSupplierReturnNote(returnData)
      
      console.log('✅ Create response:', response)
      
      if (response && (response.success === true || response.success === 'true' || response.statusCode === 201)) {
        showToast(response.message || 'Supplier return note created successfully', 'success')
        setShowCreateModal(false)
        // Reset form data
        setFormData({
          purchaseOrder: '',
          supplier: '',
          returnBy: '',
          returnedDate: format(new Date(), 'yyyy-MM-dd'),
          returnedItems: [],
          note: ''
        })
        // Try to reload data, but don't let it affect the success message
        try {
          await loadSupplierReturnNotes()
        } catch (reloadError) {
          console.warn('Failed to reload supplier return notes after creation:', reloadError)
        }
      } else {
        console.warn('⚠️ Create response indicates failure:', response)
        showToast(response?.message || 'Failed to create supplier return note', 'error')
      }
      
    } catch (error) {
      console.error('Create error:', error)
      // Extract the most specific error message available
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create supplier return note'
      showToast(errorMessage, 'error')
    } finally {
      setFormLoading(false)
    }
  }

  const handleEditSubmit = async (e) => {
    e.preventDefault()
    
    try {
      setFormLoading(true)
      
      // Validate form
      if (!formData.returnBy) {
        showToast('Please enter returned by', 'error')
        return
      }
      
      const hasValidItems = formData.returnedItems.some(item => 
        item.returnedQuantity && parseFloat(item.returnedQuantity) > 0
      )
      
      if (!hasValidItems) {
        showToast('Please add at least one item with valid quantity', 'error')
        return
      }
      
      // Transform data to match API PATCH payload structure
      const updateData = {
        returnBy: formData.returnBy,
        returnedDate: formData.returnedDate,
        note: formData.note,
        returnedItems: formData.returnedItems
          .filter(item => item.returnedQuantity && parseFloat(item.returnedQuantity) > 0)
          .map(item => ({
            ...(item.productId && { productId: item.productId }),
            ...(item.rawMaterialId && { rawMaterialId: item.rawMaterialId }),
            returnedQuantity: parseFloat(item.returnedQuantity),
            unitPrice: parseFloat(item.unitPrice || 0)
          }))
      }

      // Submit form
      const response = await supplierReturnService.updateSupplierReturnNote(selectedSupplierReturn._id, updateData)
      
      console.log('✅ Edit response:', response)
      
      if (response && (response.success === true || response.success === 'true' || response.statusCode === 200)) {
        showToast(response.message || 'Supplier return note updated successfully', 'success')
        setShowEditModal(false)
        loadSupplierReturnNotes()
      } else {
        console.warn('⚠️ Edit response indicates failure:', response)
        showToast(response?.message || 'Failed to update supplier return note', 'error')
      }
      
    } catch (error) {
      console.error('Update error:', error)
      // Extract the most specific error message available
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update supplier return note'
      showToast(errorMessage, 'error')
    } finally {
      setFormLoading(false)
    }
  }

  // Helper functions
  const formatDate = (date) => {
    if (!date) return 'N/A'
    return format(new Date(date), 'dd/MM/yyyy')
  }

  const formatCurrency = (amount) => {
    return `Rs. ${new Intl.NumberFormat('en-IN').format(amount || 0)}`
  }





  // Get statistics
  const getStatistics = () => {
    const total = supplierReturnNotes.length
    const today = new Date()
    const thisMonth = supplierReturnNotes.filter(supplierReturn => {
      const returnDate = new Date(supplierReturn.returnedDate)
      return returnDate.getMonth() === today.getMonth() && 
             returnDate.getFullYear() === today.getFullYear()
    }).length

    const totalAmount = supplierReturnNotes.reduce((sum, supplierReturn) => 
      sum + (supplierReturn.totalAmount || 0), 0
    )

    const avgAmount = total > 0 ? totalAmount / total : 0

    return { total, thisMonth, totalAmount, avgAmount }
  }

  const statistics = getStatistics()

  return (
    <div className="space-y-6">
      <div className="bg-white">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              
              Supplier Returns
            </h1>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={loadSupplierReturnNotes}
              disabled={loading}
            >
              {loading ? <ButtonSpinner size="sm" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Refresh
            </Button>
            <Button
              onClick={openCreateModal}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Return
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Total Returns */}
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <RotateCcw className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Returns</p>
                  <p className="text-2xl font-bold text-gray-900">{statistics.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* This Month */}
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Calendar className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">This Month</p>
                  <p className="text-2xl font-bold text-gray-900">{statistics.thisMonth}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total Amount */}
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-red-100 rounded-lg">
                  <DollarSign className="h-8 w-8 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Amount</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(statistics.totalAmount)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Average Return */}
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <TrendingUp className="h-8 w-8 text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Avg. Return</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(statistics.avgAmount)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Demo Notice */}
        {warehouses.length > 0 && warehouses[0]._id === '1' && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-blue-800 font-medium">Demo Mode</h3>
              <p className="text-blue-700 text-sm mt-1">
                The supplier return API is not yet implemented. This page shows the UI design and functionality 
                with sample data. All create, edit operations are simulated.
              </p>
            </div>
          </div>
        </div>
      )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-6">
            {/* Filter Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Filter className="h-5 w-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">Filter Supplier Returns</h2>
              </div>
              {(searchTerm || (branchFilter && !isManager)) && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchTerm('')
                    if (!isManager) {
                      setBranchFilter('')
                    }
                  }}
                  className="text-orange-500 border-orange-500 hover:bg-orange-50"
                >
                  Clear Filter
                </Button>
              )}
            </div>

            {/* Filter Controls */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Search Input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
                <Input
                  placeholder="Search by SRN number, PO number, supplier, warehouse..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 h-10 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                />
              </div>

              {/* Branch Filter */}
              <div className="relative">
                <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
                <select
                  value={branchFilter}
                  onChange={(e) => setBranchFilter(e.target.value)}
                  disabled={isManager}
                  className={`w-full pl-10 pr-10 py-2.5 h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none text-sm ${
                    isManager ? 'bg-gray-100 cursor-not-allowed text-gray-700' : 'bg-white'
                  }`}
                >
                  <option value="">All Branches</option>
                  {branches.map(branch => (
                    <option key={branch._id} value={branch._id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
                {/* Dropdown Arrow */}
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </div>
              </div>
              {/* {isManager && (
                <p className="text-xs text-gray-500 mt-1">
                  🔒 Managers can only view data from their assigned branch
                </p>
              )} */}
            </div>
            
            {/* Results Count */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Showing {filteredSupplierReturnNotes.length} of {supplierReturnNotes.length} supplier return notes
                {/* {isManager && userBranchId && (
                  <span className="ml-1 text-orange-600 font-medium">
                    (for your branch only)
                  </span>
                )} */}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Supplier Return Notes List */}
        <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <RotateCcw className="h-5 w-5 text-orange-600 mr-2" />
            <span>Supplier Return Notes Directory</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <InlineLoader />
            </div>
          ) : filteredSupplierReturnNotes.length > 0 ? (
            <div className="overflow-x-auto">
              {/* Desktop Table */}
              <div className="hidden lg:block">
                <table className="w-full min-w-full bg-white">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        SRN Number
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        PO Number
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Supplier/Warehouse
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Branch
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Amount
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Returned By
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredSupplierReturnNotes.map((supplierReturn) => (
                      <tr key={supplierReturn._id} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-900">
                              {formatDate(supplierReturn.returnedDate)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-orange-100 rounded-lg">
                              <RotateCcw className="h-4 w-4 text-orange-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{supplierReturn.srnNumber}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-gray-900">
                            {supplierReturn.purchaseOrder?.poNumber || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <Truck className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-900">
                              {getSupplierOrWarehouseFromReturn(supplierReturn)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <Building className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-900">
                              {supplierReturn.branch?.name || 'N/A'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-gray-900">
                              {formatCurrency(supplierReturn.totalAmount)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <User className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-900">
                              {supplierReturn.returnBy || 'N/A'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center space-x-1">
                            <button
                              onClick={() => openViewModal(supplierReturn)}
                              className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                              title="View Return"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => openEditModal(supplierReturn)}
                              className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                              title="Edit Return"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-4 p-4">
                {filteredSupplierReturnNotes.map((supplierReturn) => (
                  <div
                    key={supplierReturn._id}
                    className="bg-white border border-gray-200 rounded-lg p-4 space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-orange-100 rounded-lg">
                          <RotateCcw className="h-4 w-4 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{supplierReturn.srnNumber}</p>
                          <p className="text-sm text-gray-500">PO: {supplierReturn.purchaseOrder?.poNumber || 'N/A'}</p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-gray-500">Date</p>
                        <p className="text-gray-900">{formatDate(supplierReturn.returnedDate)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Total Amount</p>
                        <p className="text-gray-900 font-medium">{formatCurrency(supplierReturn.totalAmount)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Supplier/Warehouse</p>
                        <p className="text-gray-900 truncate">{getSupplierOrWarehouseFromReturn(supplierReturn)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Branch</p>
                        <p className="text-gray-900">{supplierReturn.branch?.name || 'N/A'}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <div className="flex items-center space-x-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          Returned by {supplierReturn.returnBy || 'N/A'}
                        </span>
                      </div>

                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => openViewModal(supplierReturn)}
                          className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                          title="View Return"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openEditModal(supplierReturn)}
                          className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                          title="Edit Return"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <RotateCcw className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Supplier Returns Found</h3>
              <p className="text-gray-500 mb-4">
                {searchTerm || (branchFilter && !isManager)
                  ? 'No supplier returns match your current filters.' 
                  : isManager
                  ? 'No supplier returns found for your branch yet.'
                  : 'Start by creating your first supplier return note.'
                }
              </p>
              {!searchTerm && !(branchFilter && !isManager) && (
                <Button
                  onClick={openCreateModal}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {isManager ? 'Create Return' : 'Create First Return'}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-lg shadow-xl">
            <div className="sticky top-0 bg-white  border-gray-200 px-6 py-4 z-10">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Create Supplier Return Note</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="px-6 pb-6">
              <CreateSupplierReturnForm
                formData={formData}
                setFormData={setFormData}
                purchaseOrders={purchaseOrders}
                onPOSelection={handlePOSelection}
                onSubmit={handleCreateSubmit}
                loading={formLoading}
                onCancel={() => setShowCreateModal(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedSupplierReturn && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-lg shadow-xl">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Edit Supplier Return Note</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="px-6 pb-6">
              <EditSupplierReturnForm
                formData={formData}
                setFormData={setFormData}
                selectedSupplierReturn={selectedSupplierReturn}
                onSubmit={handleEditSubmit}
                loading={formLoading}
                onCancel={() => setShowEditModal(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedSupplierReturn && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-lg shadow-xl">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Supplier Return Note Details</h3>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="px-6 pb-6">
              {selectedSupplierReturn && (
                <ViewSupplierReturnDetails
                  supplierReturn={selectedSupplierReturn}
                  formatDate={formatDate}
                  formatCurrency={formatCurrency}

                  onClose={() => setShowViewModal(false)}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  </div>
  )
}

// Create Supplier Return Form Component
const CreateSupplierReturnForm = ({ 
  formData, 
  setFormData, 
  purchaseOrders,
  onPOSelection,
  onSubmit, 
  loading, 
  onCancel 
}) => {
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.returnedItems]
    if (field === 'returnedQuantity' || field === 'unitPrice') {
      updatedItems[index] = { ...updatedItems[index], [field]: parseFloat(value) || 0 }
      // Calculate total price
      if (field === 'returnedQuantity' || field === 'unitPrice') {
        const qty = field === 'returnedQuantity' ? parseFloat(value) || 0 : updatedItems[index].returnedQuantity || 0
        const price = field === 'unitPrice' ? parseFloat(value) || 0 : updatedItems[index].unitPrice || 0
        updatedItems[index].totalPrice = qty * price
      }
    } else {
      updatedItems[index] = { ...updatedItems[index], [field]: value }
    }
    setFormData(prev => ({ ...prev, returnedItems: updatedItems }))
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Purchase Order *
          </label>
          <div className="relative">
            <select
              value={formData.purchaseOrder}
              onChange={(e) => onPOSelection(e.target.value)}
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 appearance-none bg-white"
              required
            >
            <option value="">Select Purchase Order</option>
            {purchaseOrders && purchaseOrders.length > 0 ? (
              purchaseOrders.map(po => (
                <option key={po._id} value={po._id}>
                  {po.poNumber} - {getSupplierOrWarehouseName(po)}
                </option>
              ))
            ) : (
              <option disabled>No Purchase Orders Available</option>
            )}
            </select>
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </div>
          </div>
          {purchaseOrders && purchaseOrders.length === 0 && (
            <p className="text-sm text-gray-500 mt-1">
              💡 Tip: Create purchase orders first to enable returns
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Supplier
          </label>
          <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
            {formData.supplier ? (
              <span className="text-gray-700">
                {getSupplierOrWarehouseName(purchaseOrders.find(po => po._id === formData.purchaseOrder)) || 'Supplier/Warehouse from Purchase Order'}
              </span>
            ) : (
              <span className="text-gray-500">Select a Purchase Order to see supplier</span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Supplier is automatically selected from the Purchase Order
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Return By *
          </label>
          <Input
            name="returnBy"
            value={formData.returnBy}
            onChange={handleInputChange}
            placeholder="Enter name of person processing return"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Return Date *
          </label>
          <Input
            type="date"
            name="returnedDate"
            value={formData.returnedDate}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>

      {formData.returnedItems.length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Return Items</h3>
          <div className="space-y-3">
            {formData.returnedItems.map((item, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="mb-3">
                  <h4 className="font-medium text-gray-900">
                    {item.productSnapshot?.name || item.rawMaterialSnapshot?.name || 'Unknown Item'}
                  </h4>
                  <p className="text-sm text-gray-500">
                    SKU: {item.productSnapshot?.sku || item.rawMaterialSnapshot?.code || 'N/A'} | 
                    Available: {item.orderedQuantity || 0} {item.productSnapshot?.unit?.abbreviation || item.rawMaterialSnapshot?.unit?.abbreviation || 'pcs'}
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Return Quantity
                    </label>
                    <Input
                      type="number"
                      min="0"
                      max={item.orderedQuantity || 0}
                      step="0.01"
                      value={item.returnedQuantity || ''}
                      onChange={(e) => handleItemChange(index, 'returnedQuantity', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Unit Price
                    </label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.unitPrice || ''}
                      onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Total Price
                    </label>
                    <Input
                      type="number"
                      value={(item.returnedQuantity * item.unitPrice) || 0}
                      readOnly
                      className="bg-gray-50"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Notes
        </label>
        <textarea
          name="note"
          value={formData.note}
          onChange={handleInputChange}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
          placeholder="Add any additional notes..."
        />
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          disabled={loading}
          className="bg-orange-500 hover:bg-orange-600"
        >
          {loading ? (
            <>
              <ButtonSpinner size="sm" className="mr-2" />
              Creating Return...
            </>
          ) : (
            <>
              <Plus className="h-4 w-4 mr-2" />
              Create Return
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

// Edit Supplier Return Form Component
const EditSupplierReturnForm = ({ 
  formData, 
  setFormData, 
  selectedSupplierReturn,
  onSubmit, 
  loading, 
  onCancel 
}) => {
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.returnedItems]
    if (field === 'returnedQuantity' || field === 'unitPrice') {
      updatedItems[index] = { ...updatedItems[index], [field]: parseFloat(value) || 0 }
      // Calculate total price
      const qty = field === 'returnedQuantity' ? parseFloat(value) || 0 : updatedItems[index].returnedQuantity || 0
      const price = field === 'unitPrice' ? parseFloat(value) || 0 : updatedItems[index].unitPrice || 0
      updatedItems[index].totalPrice = qty * price
    } else {
      updatedItems[index] = { ...updatedItems[index], [field]: value }
    }
    setFormData(prev => ({ ...prev, returnedItems: updatedItems }))
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            SRN Number
          </label>
          <Input
            value={selectedSupplierReturn?.srnNumber || ''}
            readOnly
            className="bg-gray-50"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            PO Number
          </label>
          <Input
            value={selectedSupplierReturn?.purchaseOrder?.poNumber || ''}
            readOnly
            className="bg-gray-50"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Return By *
          </label>
          <Input
            name="returnBy"
            value={formData.returnBy}
            onChange={handleInputChange}
            placeholder="Enter name of person processing return"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Return Date *
          </label>
          <Input
            type="date"
            name="returnedDate"
            value={formData.returnedDate}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>

      {formData.returnedItems.length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Return Items</h3>
          <div className="space-y-3">
            {formData.returnedItems.map((item, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="mb-3">
                  <h4 className="font-medium text-gray-900">
                    {item.productSnapshot?.name || item.rawMaterialSnapshot?.name || 'Unknown Item'}
                  </h4>
                  <p className="text-sm text-gray-500">
                    SKU: {item.productSnapshot?.sku || item.rawMaterialSnapshot?.code || 'N/A'} | 
                    Available: {item.orderedQuantity || 0} {item.productSnapshot?.unit?.abbreviation || item.rawMaterialSnapshot?.unit?.abbreviation || 'pcs'}
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Return Quantity
                    </label>
                    <Input
                      type="number"
                      min="0"
                      max={item.orderedQuantity || 0}
                      step="0.01"
                      value={item.returnedQuantity || ''}
                      onChange={(e) => handleItemChange(index, 'returnedQuantity', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Unit Price
                    </label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.unitPrice || ''}
                      onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Total Price
                    </label>
                    <Input
                      type="number"
                      value={(item.returnedQuantity * item.unitPrice) || 0}
                      readOnly
                      className="bg-gray-50"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Notes
        </label>
        <textarea
          name="note"
          value={formData.note}
          onChange={handleInputChange}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
          placeholder="Add any additional notes..."
        />
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          disabled={loading}
          className="bg-orange-500 hover:bg-orange-600"
        >
          {loading ? (
            <>
              <ButtonSpinner size="sm" className="mr-2" />
              Updating Return...
            </>
          ) : (
            <>
              <Edit className="h-4 w-4 mr-2" />
              Update Return
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

// View Supplier Return Details Component
const ViewSupplierReturnDetails = ({ 
  supplierReturn, 
  formatDate, 
  formatCurrency,

  onClose 
}) => {
  return (
    <div className="space-y-6">
      {/* Return Information */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
            <FileText className="h-5 w-5 mr-2 text-blue-600" />
            Return Information
          </h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">SRN Number</span>
              <span className="font-medium text-gray-900">{supplierReturn.srnNumber}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">PO Number</span>
              <span className="font-medium text-gray-900">{supplierReturn.purchaseOrder?.poNumber || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Return Date</span>
              <span className="font-medium text-gray-900">{formatDate(supplierReturn.returnedDate)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Return Reason</span>
              <span className="font-medium text-gray-900">{supplierReturn.returnBy || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Created Date</span>
              <span className="font-medium text-gray-900">{formatDate(supplierReturn.createdAt)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Created By</span>
              <span className="font-medium text-gray-900">{supplierReturn.createdBy?.username || 'N/A'}</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
            <Building className="h-5 w-5 mr-2 text-green-600" />
            Supplier & Location
          </h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Supplier</span>
              <span className="font-medium text-gray-900">{getSupplierOrWarehouseFromReturn(supplierReturn)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Requesting Branch</span>
              <span className="font-medium text-gray-900">{supplierReturn.branch?.name || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Returned By</span>
              <span className="font-medium text-gray-900">{supplierReturn.returnedBy || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Total Amount</span>
              <span className="font-medium text-gray-900 text-lg">{formatCurrency(supplierReturn.totalAmount)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Notes */}
      {supplierReturn.note && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
            <FileText className="h-5 w-5 mr-2 text-purple-600" />
            Notes
          </h3>
          <p className="text-sm text-gray-700 whitespace-pre-wrap bg-white p-3 rounded border">
            {supplierReturn.note}
          </p>
        </div>
      )}

      {/* Returned Items */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
          <Package className="h-5 w-5 mr-2 text-orange-600" />
          Returned Items ({supplierReturn.returnedItems?.length || 0})
        </h3>
        
        {supplierReturn.returnedItems && supplierReturn.returnedItems.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Item</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Type</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-700">Available Qty</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-700">Returned Qty</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-700">Unit Price</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-700">Total</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Return Reason</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {supplierReturn.returnedItems.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <div>
                        <p className="font-medium text-gray-900">
                          {item.productSnapshot?.name || item.rawMaterialSnapshot?.name || 'Unknown'}
                        </p>
                        <p className="text-gray-500 text-xs">
                          {item.productSnapshot?.sku || item.rawMaterialSnapshot?.code || 'N/A'}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        item.itemType === 'product' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {item.itemType === 'product' ? 'Product' : 'Raw Material'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right font-mono">
                      {item.orderedQuantity || 0} {item.productSnapshot?.unit?.abbreviation || item.rawMaterialSnapshot?.unit?.abbreviation || 'pcs'}
                    </td>
                    <td className="px-4 py-2 text-right font-mono">
                      {item.returnedQuantity || 0} {item.productSnapshot?.unit?.abbreviation || item.rawMaterialSnapshot?.unit?.abbreviation || 'pcs'}
                    </td>
                    <td className="px-4 py-2 text-right font-mono">
                      {formatCurrency(item.unitPrice)}
                    </td>
                    <td className="px-4 py-2 text-right font-mono font-medium">
                      {formatCurrency(item.totalPrice)}
                    </td>
                    <td className="px-4 py-2">

                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan="5" className="px-4 py-2 text-right font-medium">
                    Total Amount:
                  </td>
                  <td className="px-4 py-2 text-right font-mono font-bold text-lg">
                    {formatCurrency(supplierReturn.totalAmount)}
                  </td>
                  <td className="px-4 py-2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No items found</p>
        )}
      </div>

      {/* Close Button */}
      <div className="flex justify-end">
        <Button onClick={onClose} variant="outline">

          Close
        </Button>
      </div>
    </div>
  )
}

export default SupplierReturn